import React from 'react';
import { Document, Page } from 'react-pdf';
import { FileText } from 'lucide-react';

export interface NecaModeViewerProps {
  file: { url: string; name?: string } | null;
  estimationId?: string;
  necaPageNumber: number;
  necaNumPages: number | null;
  necaScale: number;
  setNecaNumPages: (n: number) => void;
  setNecaPageNumber: (n: number | ((p: number) => number)) => void;
  pdfOptions: Record<string, unknown>;
}

export const NecaModeViewer: React.FC<NecaModeViewerProps> = ({
  file,
  estimationId,
  necaPageNumber,
  necaNumPages,
  necaScale,
  setNecaNumPages,
  setNecaPageNumber,
  pdfOptions,
}) => {
  if (!file?.url) {
    return (
      <div
        className="pdf-annotator-wrapper"
        style={{
          padding: 0,
          margin: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: '1rem'
        }}>
          <FileText size={48} color="#ccc" />
          <p style={{ color: 'var(--text-muted)' }}>NECA Manual not loaded</p>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-placeholder)' }}>File URL: {file?.url || 'undefined'}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pdf-annotator-wrapper"
      style={{
        padding: 0,
        margin: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'auto',
        background: 'var(--border-color)'
      }}>
        <div className="pdf-page-container" style={{ padding: '2rem 0' }}>
          <Document
            key="neca-manual-document"
            file={file.url}
            onLoadSuccess={(pdf: any) => {
              console.log('✅ NECA PDF loaded successfully:', pdf.numPages, 'pages');
              setNecaNumPages(pdf.numPages);
              if (estimationId) {
                const storageKey = `neca-page-number-${estimationId}`;
                const savedPage = localStorage.getItem(storageKey);
                if (savedPage) {
                  const pageNum = parseInt(savedPage, 10);
                  if (pageNum >= 1 && pageNum <= pdf.numPages) {
                    setNecaPageNumber(pageNum);
                    console.log(`📖 Restored saved NECA page number: ${pageNum}`);
                  } else {
                    setNecaPageNumber(1);
                  }
                } else {
                  setNecaPageNumber(1);
                }
              } else {
                setNecaPageNumber(1);
              }
            }}
            onLoadError={(error: any) => {
              console.error('❌ NECA PDF load error:', error);
            }}
            options={pdfOptions}
            loading={
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem'
              }}>
                <div className="loading-spinner" style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #666',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </div>
            }
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {necaNumPages && necaPageNumber <= necaNumPages ? (
                <Page
                  pageNumber={necaPageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  scale={necaScale}
                  loading={<div />}
                  error={<div />}
                  onLoadSuccess={(page: any) => {
                    const nativeRotation = page.rotate || 0;
                    if (nativeRotation !== 0) {
                      console.log(`📄 NECA page ${necaPageNumber} has native rotation: ${nativeRotation}°`);
                    }
                  }}
                />
              ) : null}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
};
